import {
  Button,
  Checkbox,
  CheckboxProps,
  DatePicker,
  Input,
  Modal,
  Select,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { WarningFilled, InfoCircleTwoTone } from '@ant-design/icons';
import { createObjReq, menteeObjectiveProps, USER_ROLES } from '../../types';
import { Statuses, objectiveStatuses, statusesLabel } from '../../helper';
import {
  createObjective,
  getObjective,
  updateObjectives,
} from '../../services/objectiveServices';
import { useAtomValue } from 'jotai';
import { userAtom, UserProps, globalAtom, globalProps } from '../../store/atom';
import { useMsal } from '@azure/msal-react';
import { Label, Loading, NotificationAlert } from '..';

const { TextArea } = Input;

interface Props {
  open: boolean;
  setOpen(val: boolean): void;
  editID: string | null;
  setCallagain?(val: boolean): void;
  from?: USER_ROLES;
}

const defaulData = {
  description: '',
  objectiveCategory: '',
  expectedOutcome: '',
  status: Statuses.Draft,
  startDate: dayjs().format('YYYY-MM-DD'),
  endDate: '',
  reason: '',
};

const CreateObjective = ({
  open,
  setOpen,
  editID,
  setCallagain,
  from,
}: Props) => {
  const [data, setData] = useState<createObjReq>({ ...defaulData });
  const [fillData, setfillData] = useState(false);
  const { selectedMentee } = useAtomValue<UserProps>(userAtom);
  const { userInfo } = useAtomValue<globalProps>(globalAtom);
  const [loading, setLoading] = useState(false);
  const { accounts } = useMsal();
  const [editData, setEditData] = useState<menteeObjectiveProps>();
  const [fetchLoading, setFetchLoading] = useState(true);
  const [isChangedorNull, setIsChangedorNull] = useState(true);
  const [checked, setChecked] = useState(false);
  const myRef = useRef<any>();

  const getObjData = async () => {
    setData({ ...defaulData });
    setChecked(false);
    setFetchLoading(true);
    const { error, data } = await getObjective(editID || '');
    setFetchLoading(false);
    if (error) return myRef.current?.call('error', error?.message);
    if (data) {
      setData({
        description: data?.description || '',
        objectiveCategory: data?.objectiveCategory || '',
        expectedOutcome: data?.expectedOutcome || '',
        status: data?.status || objectiveStatuses[0].value,
        startDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs(data?.endDate).format('YYYY-MM-DD') || '',
        reason: '',
      });
      setEditData(data);
    }
  };

  // to disable save button if any field is empty
  const checkIsNullOrChanged = () => {
    if (editID) {
      if (checked) {
        setIsChangedorNull(false);
      } else if (
        editData?.status !== Statuses.Draft
          ? Object.values(data).some((i) => i === '')
          : Object.entries(data).some(
              ([key, value]) => key !== 'reason' && value === ''
            )
      ) {
        setIsChangedorNull(true);
      } else if (
        data.description === editData?.description &&
        data.objectiveCategory === editData?.objectiveCategory &&
        data.expectedOutcome === editData?.expectedOutcome &&
        data.status === editData?.status &&
        data.startDate === dayjs(editData?.startDate).format('YYYY-MM-DD') &&
        data.endDate === dayjs(editData?.endDate).format('YYYY-MM-DD') &&
        (editData?.status === Statuses.Draft
          ? true
          : data.reason === editData?.reason) &&
        !checked
      ) {
        setIsChangedorNull(true);
      } else {
        setIsChangedorNull(false);
      }
    }
    if (!editID) {
      if (
        Object.entries(data).some(
          ([key, value]) => key !== 'reason' && value === ''
        )
      ) {
        setIsChangedorNull(true);
      } else {
        setIsChangedorNull(false);
      }
    }
  };

  useEffect(() => {
    checkIsNullOrChanged();
  }, [data, checked]);

  useEffect(() => {
    if (editID !== null) {
      getObjData();
    } else {
      setData({ ...defaulData });
    }
  }, [editID, open]);

  const handleChange = (field: string, value: string) => {
    setData({ ...data, [field]: value });
  };

  const dateChange = (dateString: string | string[], field: string) => {
    setData({ ...data, [field]: dateString });
  };

  const checkValidation = () => {
    let ret = true;
    setfillData(false);
    Object.entries(data).forEach((val) => {
      if (
        val[0] === 'reason' &&
        data.status !== Statuses.Draft &&
        val[1] === ''
      )
        ret = false;
      if (val[0] !== 'reason' && val[1] === '') ret = false;
    });
    return ret;
  };

  // submit function
  const handleSubmit = async () => {
    if (!checkValidation()) {
      return setfillData(true);
    }

    setLoading(true);
    if (editID !== null && editData) {
      const { error } = await updateObjectives(
        {
          ...editData,
          ...data,
          ...(data.status === Statuses.Draft && checked
            ? { status: Statuses.NotStarted }
            : {}),
          startDate: editData.startDate,
        },
        editID
      );
      setLoading(false);
      if (error) return myRef.current?.call('error', error?.message);
      setOpen(false);
      setCallagain?.(true);
      myRef.current?.call('success');
    } else if (from === USER_ROLES.MENTEE) {
      const { error } = await createObjective({
        ...data,
        menteeId: accounts[0]?.localAccountId,
        mentorId: userInfo?.mentor?.id,
      });
      setLoading(false);
      // check if the error is 489, then call the error function
      if (error?.status === 489) {
        setOpen(false);
        setCallagain?.(true);
        return myRef.current?.call('error', error?.message);
      }
      // if there is an error, call the error function
      if (error) return myRef.current?.call('error', error?.message);

      // if there is no error, call the success function
      setOpen(false);
      setData({ ...defaulData });
      setCallagain?.(true);
      myRef.current?.call('success');
    } else if (from === USER_ROLES.MENTOR) {
      const { error } = await createObjective({
        ...data,
        mentorId: accounts[0]?.localAccountId,
        menteeId: selectedMentee?.id,
      });
      setLoading(false);
      if (error) return myRef.current?.call('error', error?.message);

      setOpen(false);
      setData({ ...defaulData });
      setCallagain?.(true);
      myRef.current?.call('success');
    }
  };

  const handleActive: CheckboxProps['onChange'] = (e) => {
    setChecked(e.target.checked);
  };

  return (
    <>
      <Modal
        title={
          <div className="text-[20px] font-[500]">
            {editID !== null ? 'Edit Objective' : 'Create Objective'}
          </div>
        }
        centered
        open={open}
        onOk={() => setOpen(false)}
        onCancel={() => setOpen(false)}
        width={1024}
        footer={<></>}
      >
        {fetchLoading && editID !== null ? (
          <Loading />
        ) : (
          <div>
            <div className="py-6 flex flex-col gap-y-6 mb-[30px]">
              {data.status !== Statuses.Draft ? (
                <div className="mb-[-10px] flex gap-x-2 opacity-45">
                  <InfoCircleTwoTone />{' '}
                  <span>
                    {data.status === Statuses.NCExpired
                      ? 'This objective is expired.'
                      : 'This objective is active.'}
                  </span>
                </div>
              ) : null}
              <div className="grid grid-cols-5 gap-x-[10px]">
                <Label label="Description" className="col-span-3">
                  <TextArea
                    style={{ height: 30 }}
                    placeholder="Write a description about this objective"
                    onChange={(e) =>
                      handleChange('description', e.target.value)
                    }
                    value={data.description}
                    disabled={editID !== null && data.status !== Statuses.Draft}
                  />
                </Label>
                <Label label="Category" className="col-span-2">
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select Category"
                    onChange={(val) => handleChange('objectiveCategory', val)}
                    value={data.objectiveCategory}
                    options={[
                      { value: 'Organizational', label: 'Organizational' },
                      { value: 'Project Based', label: 'Project Based' },
                      {
                        value: 'Personal Development',
                        label: 'Personal Development',
                      },
                    ]}
                    disabled={editID !== null && data.status !== Statuses.Draft}
                  />
                </Label>
              </div>
              <div className="grid grid-cols-5 gap-x-[10px]">
                <Label label="Expected Outcome" className="col-span-5">
                  <TextArea
                    placeholder="What are the outcomes expected of this objective?"
                    onChange={(e) =>
                      handleChange('expectedOutcome', e.target.value)
                    }
                    value={data.expectedOutcome}
                    disabled={editID !== null && data.status !== Statuses.Draft}
                  />
                </Label>
              </div>

              <div className="grid grid-cols-3 gap-x-[10px]">
                {editID !== null ? (
                  <Label label="Status" className="">
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Select Status"
                      onChange={(val) => handleChange('status', val)}
                      value={
                        data.status === Statuses.Draft
                          ? 'Draft'
                          : data.status === Statuses.Cancelled
                            ? 'Cancelled'
                            : data.status === Statuses.NCExpired
                              ? 'Not Completed - Expired'
                              : data.status
                      }
                      options={
                        from === USER_ROLES.MENTEE
                          ? objectiveStatuses.filter(
                              (status) =>
                                status.label !==
                                statusesLabel[Statuses.Cancelled]
                            )
                          : [...objectiveStatuses]
                      }
                      disabled={
                        editID !== null &&
                        (data.status === Statuses.Draft ||
                          data.status === Statuses.NCExpired)
                      }
                    />
                  </Label>
                ) : null}
                <Label label="Target Completion Date" className="">
                  <DatePicker
                    onChange={(_, dateString) =>
                      dateChange(dateString, 'endDate')
                    }
                    className="w-full"
                    placeholder="Select date"
                    value={data.endDate === '' ? null : dayjs(data.endDate)}
                    minDate={dayjs(data.startDate)}
                    disabled={
                      editID !== null &&
                      from === USER_ROLES.MENTEE &&
                      data.status !== Statuses.Draft
                    }
                  />
                </Label>
              </div>
              {editID !== null ? (
                <>
                  {data.status !== Statuses.Draft ? (
                    <div>
                      <Label label="Reason for Change" className="=">
                        <TextArea
                          placeholder=""
                          onChange={(e) =>
                            handleChange('reason', e.target.value)
                          }
                          value={data.reason}
                          disabled={data.status === Statuses.NCExpired}
                        />
                      </Label>
                    </div>
                  ) : (
                    <div>
                      <div className="flex gap-x-2">
                        <Checkbox
                          onChange={handleActive}
                          checked={checked}
                          disabled={from === USER_ROLES.MENTEE}
                        >
                          {from === USER_ROLES.MENTEE &&
                            'Activate this Objective (Please discuss this objective with your mentor so they can activate it for you)'}
                          {from === USER_ROLES.MENTOR &&
                            'Activate this Objective'}
                        </Checkbox>
                      </div>
                      <div className="flex gap-x-2">
                        <WarningFilled className="text-[#FAAD14] text-[16px]" />
                        <span className="opacity-45">
                          After activation, this objective cannot be deleted and
                          only certain fields will be editable.
                        </span>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
            <div className="flex">
              {fillData ? (
                <div className="text-[red]">Please fill all the fields.</div>
              ) : null}
              <div className="flex gap-x-2 ml-auto">
                <Button onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={loading || isChangedorNull}
                >
                  {editID === null ? 'Create' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
      <NotificationAlert ref={myRef} />
    </>
  );
};

export default CreateObjective;
